import { useCallback, useMemo } from 'react';

import { HStack, IconButton, Text } from '@vimeo/bokeh';
import { AiSparkles, ChevronLeftSmall, CloseX } from '@vimeo/bokeh-icons';

import type { TOverrideBreakpoints, TPanels } from './constants';
import { ViewerQnAPanels } from './constants';
import { ViewerQnAHeaderWrapper } from './ViewerQnA.styles';
import * as finalCopy from '../../copy/final';

interface IViewerQnAHeader {
  onClose?: () => void;
  onBack: (panel: TPanels) => void;
  activePanel: string;
  overrideBreakpoint?: TOverrideBreakpoints;
}

export const ViewerQnAHeader = ({
  onClose,
  activePanel,
  onBack,
  overrideBreakpoint,
}: IViewerQnAHeader) => {
  const headerTitle = useMemo(() => {
    switch (activePanel) {
      case ViewerQnAPanels.ASK_QUESTION:
        return finalCopy.generatingAnswer;
      case ViewerQnAPanels.ANSWER:
        return finalCopy.answer;
      case ViewerQnAPanels.AI_ACTIVATION:
        return finalCopy.activatingAiShort;
      default:
        return finalCopy.vimeoAi;
    }
  }, [activePanel]);

  const onBackClick = useCallback(() => {
    onBack(ViewerQnAPanels.QUESTIONS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerStyles = useMemo(() => {
    switch (overrideBreakpoint) {
      case 'xxs':
      case 'xs':
        return { padding: '8px 8px 8px 12px', fontSize: '14px', iconSize: 'sm' };
      case 'sm':
        return { padding: '8px 16px 8px 16px', fontSize: '16px', iconSize: 'sm' };
      case 'md':
        return { padding: '12px 16px 12px 16px', fontSize: '18px', iconSize: 'sm' };
      case 'lg':
        return { padding: '16px 24px 16px 24px', fontSize: '18px', iconSize: 'sm' };
      case 'xl':
        return { padding: '16px 24px 16px 24px', fontSize: '18px', iconSize: 'sm' };
      case 'xxl':
        return { padding: '16px 24px 16px 24px', fontSize: '24px', iconSize: 'md' };
      default:
        return { padding: { base: 200, md: 300 }, fontSize: 'inherit', iconSize: 'md' };
    }
  }, [overrideBreakpoint]);

  return (
    <ViewerQnAHeaderWrapper
      align="center"
      justify={'space-between'}
      pb={overrideBreakpoint ? 'inherit' : 100}
      p={headerStyles.padding}
      width={'100%'}
      position="relative"
    >
      {(activePanel === ViewerQnAPanels.ASK_QUESTION || activePanel === ViewerQnAPanels.ANSWER) && (
        <IconButton
          aria-label={'Back'}
          icon={<ChevronLeftSmall />}
          variant={'tertiary'}
          size={headerStyles.iconSize}
          onClick={onBackClick}
          isDisabled={activePanel === ViewerQnAPanels.ASK_QUESTION}
          position="absolute"
        />
      )}
      <HStack
        flexWrap={'nowrap'}
        m={
          activePanel === ViewerQnAPanels.QUESTIONS || activePanel === ViewerQnAPanels.LOADER
            ? 'unset'
            : '0 auto'
        }
      >
        {activePanel !== ViewerQnAPanels.ASK_QUESTION ? <AiSparkles color="text-primary" /> : null}
        <Text
          variant={overrideBreakpoint ? undefined : 'heading-md'}
          as="h4"
          fontSize={headerStyles.fontSize}
          color="text-primary"
        >
          {headerTitle}
        </Text>
      </HStack>
      {onClose && (
        <IconButton
          aria-label="Close"
          variant="tertiary"
          size={headerStyles.iconSize}
          icon={<CloseX />}
          onClick={onClose}
        />
      )}
    </ViewerQnAHeaderWrapper>
  );
};
